import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge, Input, Label } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import {
  useAccountVehiclePurchases,
  useAccountVehiclePurchase,
  useDeleteAccountVehiclePurchase,
} from "hooks/useAccountVehiclePurchase"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"

import AccountVehiclePurchaseAddEdit from "./AccountVehiclePurchaseAddEdit"
import AccountVehiclePurchaseHeader from "./AccountVehiclePurchaseHeader"
import AccountVehiclePurchaseView from "./AccountVehiclePurchaseView"

const AccountVehiclePurchaseList = () => {
  document.title = "Account Vehicle Purchase List"

  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const [sourceType, setSourceType] = useState("")

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search, ...(sourceType && { source_type: sourceType }) }
  }, [params, sourceType])

  const { data: responseData, isLoading, refetch, isRefetching } =
    useAccountVehiclePurchases(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountVehiclePurchase(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteAccountVehiclePurchase()

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) setSelectedData(null)
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Vehicle purchase deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete vehicle purchase.")
    }
  }

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Importer", cell: ({ row }) => row.original.vehicle_importer?.name || row.original.vehicleImporter?.name || "-" },
      { header: "Vehicle", cell: ({ row }) => row.original.vehicle?.stock_id || row.original.vehicle?.full_chassis || "-" },
      {
        header: "Source",
        cell: ({ row }) =>
          row.original.source_type === "bank_lc" ? (
            <Badge color="info">Bank LC{row.original.lc_number ? ` #${row.original.lc_number}` : ""}</Badge>
          ) : (
            <Badge color="secondary">Importer</Badge>
          ),
      },
      { header: "Price", cell: ({ row }) => <Badge color="light" className="text-dark">{formatCurrency(row.original.purchase_price || 0, "BDT")}</Badge> },
      {
        header: "Date",
        cell: ({ row }) =>
          row.original.purchase_date
            ? formatReadableDate(row.original.purchase_date)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button color="primary" size="sm" onClick={() => handleViewClick(row.original)} id={`view-account-vehicle-purchase-${row.original.id}`}>
                <i className="mdi mdi-eye-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`view-account-vehicle-purchase-${row.original.id}`}>View</UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.ACCOUNT_VEHICLE_PURCHASE.EDIT]}>
              <li>
                <Button color="info" size="sm" onClick={() => setSelectedData(row.original) || setModal(true)} id={`edit-account-vehicle-purchase-${row.original.id}`}>
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`edit-account-vehicle-purchase-${row.original.id}`}>Edit</UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ACCOUNT_VEHICLE_PURCHASE.DELETE]}>
              <li>
                <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-account-vehicle-purchase-${row.original.id}`}>
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`delete-account-vehicle-purchase-${row.original.id}`}>Delete</UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Vehicle Purchase" breadcrumbItem="Vehicle Purchase List">
      <AccountVehiclePurchaseHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3" />
      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <Row className="mb-3 align-items-end">
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
        <Col sm={3}>
          <Label className="mb-1 small fw-semibold">Source</Label>
          <Input type="select" bsSize="sm" value={sourceType} onChange={e => setSourceType(e.target.value)}>
            <option value="">All Sources</option>
            <option value="importer">Importer</option>
            <option value="bank_lc">Bank LC</option>
          </Input>
        </Col>
      </Row>

      <TableContainer columns={columns} data={data} isPagination={true} paginationInfo={paginationInfo} onPageChange={setPage} loading={isLoading || isRefetching} />

      <AccountVehiclePurchaseView modal={viewModal} toggleModal={toggleViewModal} purchase={viewResponse?.data} loading={isViewLoading} />

      <AccountVehiclePurchaseAddEdit modal={modal} isEdit={!!selectedData} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default AccountVehiclePurchaseList
